import React, { useState, useEffect, useRef, useMemo } from "react";
import { PageHeaderActions, usePageHeaderActionsSlot } from "../../../../components/Tabella/pageHeaderActions";
import { Row, Col, Card, Modal, Button, Spinner, Form } from "react-bootstrap";
import chiediConferma from '../../../../components/Util/chiediConferma';
import { useNavigate, useParams } from 'react-router-dom';
import { DatiAllievo, nomeAllievo, useJunior } from "../../../../components/Ordini/DatiAllievo";
import MyApiClient from "../../../../../api-client/MyApiClient";
import Select from "react-select";
import { format } from 'date-fns';
import { italianProvinces } from "../../../../components/Util/enum";
import { sidSelectStyles } from "../../../../components/Util/sidSelectStyles";

import { conMotivo } from "../../../../components/Util/erroreServer";
import { useProfile } from "../../../../../hooks/useProfile";
import { usePageTitle, useStatoInSolaLettura } from "../../../../layouts/PageHeaderExtra";
import { apriFile } from '../../../../components/Util/anteprimaFile';
import { segnalaMancanti } from '../../../../components/Util/campiMancanti';
import { CronologiaOrdine, passiCronologia, SCHEDA_ORDINE } from "../../../../components/Vendite/CronologiaOrdine";
// Date e importi in formato italiano; vuoti se il valore manca o non e' valido
const toDate = (value) => {
    if (!value) return null;
    const date = new Date(value);
    return isNaN(date.getTime()) ? null : date;
};
const fmtDate = (value) => {
    const date = toDate(value);
    return date ? format(date, 'dd/MM/yyyy') : '';
};
const toAmount = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const amount = Number(value);
    return isNaN(amount) ? null : amount;
};
const fmtEuro = (value) => {
    const amount = toAmount(value);
    return amount === null ? '-' : amount.toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });
};

/*
 * Righe e riepilogo IVA letti dall'XML della fattura elettronica (FatturaPA):
 * l'API non li restituisce a parte. Se l'XML manca o non si legge si torna al solo importo.
 */
const parseInvoiceXml = (xml) => {
    if (!xml || typeof DOMParser === 'undefined') return null;
    try {
        const doc = new DOMParser().parseFromString(xml, 'application/xml');
        if (doc.getElementsByTagName('parsererror').length) return null;
        const find = (root, name) => Array.from(root.getElementsByTagName('*')).filter(el => el.localName === name);
        const text = (root, name) => (find(root, name)[0]?.textContent || '').trim();
        const lines = find(doc, 'DettaglioLinee').map(el => ({
            description: text(el, 'Descrizione'),
            quantity: toAmount(text(el, 'Quantita')),
            total: toAmount(text(el, 'PrezzoTotale')),
            vatRate: toAmount(text(el, 'AliquotaIVA')),
            nature: text(el, 'Natura'),
        }));
        const summaries = find(doc, 'DatiRiepilogo');
        const sum = (name) => summaries.reduce((acc, el) => acc + (toAmount(text(el, name)) || 0), 0);
        return {
            lines,
            taxable: summaries.length ? sum('ImponibileImporto') : null,
            vat: summaries.length ? sum('Imposta') : null,
            total: toAmount(text(doc, 'ImportoTotaleDocumento')),
        };
    } catch (e) {
        return null;
    }
};

export const UserTransactionsEdit = () => {

    const { id } = useParams();
    const navigate = useNavigate();
    const { profile } = useProfile();
    const isFatturazioneConfirmed = profile?.club?.invoiceDataSender?.status === 'CONFIRMED';
    const formRefs = useRef({});
    // Incrementato per ricaricare i dati senza ricaricare la pagina
    const [reloadKey, setReloadKey] = useState(0);
    // Risposta completa dell'API: alimenta intestazione, articolo e cronologia
    const [transaction, setTransaction] = useState(null);
    // L'ordine del documento (abbonamento, corso, lezione o impianto): prodotto e cronologia
    const [ordine, setOrdine] = useState(null);
    // Modifica del destinatario aperta nel box a destra; copia dei dati per "Annulla"
    const [editingReceiver, setEditingReceiver] = useState(false);
    const [receiverBackup, setReceiverBackup] = useState(null);

    const [loadingShowed, setLoadingShowed] = useState(false);
    // Operazione da eseguire con «Continua» nella finestra di conferma

    const optionsFatturazione = [
        { value: "privato", label: "Privato" },
        { value: "azienda", label: "Azienda" }
    ]

    const [modalShowed, setModalShowed] = useState(false);
    const [modalTitle, setModalTitle] = useState('');
    const [modalText, setModalText] = useState('');

    // Il documento punta al suo ordine con uno di questi campi: lo si legge per il prodotto e la cronologia
    const leggiOrdine = async (apiClient, token, documento) => {
        const collegamenti = [
            ['subscription', documento?.user_subscription_id, (id) => apiClient.getUserBookingSubscriptionById(token, id)],
            ['course', documento?.user_course_id, (id) => apiClient.getUserBookingCourseById(token, id)],
            ['lesson', documento?.user_lesson_id, (id) => apiClient.getUserBookingLessonById(token, id)],
            ['venue', documento?.user_venue_id, (id) => apiClient.getUserBookingVenueById(token, id)],
        ];
        const trovato = collegamenti.find(([, idOrdine]) => idOrdine);
        if (!trovato) return;
        const [tipo, idOrdine, leggi] = trovato;
        try {
            const risposta = await leggi(idOrdine);
            if (risposta && !risposta.error && risposta.id) setOrdine({ tipo, dati: risposta });
        } catch (errore) {
            console.error("Ordine del documento non letto", errore);
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                const tokenDetailsString = sessionStorage.getItem('userDetails');
                if (tokenDetailsString) {
                    const tokenDetails = JSON.parse(tokenDetailsString);
                    const token = tokenDetails.idToken;
                    const apiClient = new MyApiClient();

                    setLoadingShowed(true);
                    const [userBookingTransactions] = await Promise.all([
                        apiClient.getUserBookingTransactionById(token, id)
                    ]);
                    setLoadingShowed(false);

                    if(userBookingTransactions) {

                        let type_document = null;
                        if(userBookingTransactions.type === 'INVOICE') {
                            type_document = 'Fattura';
                        } else if(userBookingTransactions.type === 'RECEIPT') {
                            type_document = 'Ricevuta';
                        }

                        let status = null;
                        if(userBookingTransactions.invoice_status === 'SENT') {
                            status = {
                                "label": "Inviato",
                                "class": "text-primary"
                            };
                        } else if(userBookingTransactions.invoice_status === 'ACCEPTED') {
                            status = {
                                "label": "Accettato",
                                "class": "text-success"
                            };
                        } else if(userBookingTransactions.invoice_status === 'REJECTED') {
                            status = {
                                "label": "Rifiutato",
                                "class": "text-danger"
                            };
                        } else if(userBookingTransactions.invoice_status === 'NOT_SENT') {
                            status = {
                                "label": "Non inviato",
                                "class": "text-warning"
                            };
                        } else if(userBookingTransactions.invoice_status === 'NOT_GENERATED') {
                            status = {
                                "label": "Non generato",
                                "class": "text-error"
                            };
                        }

                        const elementFormData = {
                            id: userBookingTransactions.id,
                            playerId: userBookingTransactions.playerId,
                            playerName: userBookingTransactions.playerName,
                            type: userBookingTransactions.type,
                            type_document: type_document,
                            invoice_number: userBookingTransactions.invoice_number_str,
                            status: status,
                            pdf_document: userBookingTransactions.pdf_document,
                            xml_document: userBookingTransactions.xml_document,
                            status_label: userBookingTransactions.invoice_status,
                            sdi_notification: userBookingTransactions.sdi_notification,
                            sdi_rejection_errors: userBookingTransactions.sdi_rejection_errors
                        };

                        if(userBookingTransactions.invoice_status === 'REJECTED' && userBookingTransactions.playerId) {
                            try {
                                const playerInvoiceData = await apiClient.getClubPlayerInvoiceDataReceiver(token, userBookingTransactions.playerId);
                                if (playerInvoiceData && !playerInvoiceData.error) {
                                    userBookingTransactions.receiver = playerInvoiceData;
                                }
                            } catch (e) {
                                console.error("Error fetching player invoice data:", e);
                            }
                        }

                        if(userBookingTransactions.receiver) {
                            elementFormData.codice_fiscale = userBookingTransactions.receiver.tax_code;
                            elementFormData.email = userBookingTransactions.receiver.pec;
                            elementFormData.indirizzo = userBookingTransactions.receiver.address;
                            elementFormData.cap = userBookingTransactions.receiver.zip_code;
                            elementFormData.citta = userBookingTransactions.receiver.city;
                            elementFormData.provincia = userBookingTransactions.receiver.province;
                            if(userBookingTransactions.receiver.type === 'consumer') {
                                elementFormData.tipo_cliente = 'privato';
                                elementFormData.nome = userBookingTransactions.receiver.first_name;
                                elementFormData.cognome = userBookingTransactions.receiver.last_name;
                            } else if(userBookingTransactions.receiver.type === 'business') {
                                elementFormData.tipo_cliente = 'azienda';
                                elementFormData.ragione_sociale = userBookingTransactions.receiver.denomination;
                                elementFormData.partita_iva = userBookingTransactions.receiver.vat_number;
                                elementFormData.codice_destinatario = userBookingTransactions.receiver.sdi_code;
                            }
                        }

                        setTransaction(userBookingTransactions);
                        leggiOrdine(apiClient, token, userBookingTransactions);
                        setFormData(elementFormData);
                    }
                }
            } catch (error) {
                console.error("Error fetching data:", error);
            }
        };

        fetchData();
    }, [id, reloadKey]);

    const [formData, setFormData] = useState({
        id: '',
        playerId: '',
        playerName: '',
        type: '',
        type_document: '',
        invoice_number: '',
        status: {},
        status_label: '',
        pdf_document: '',
        xml_document: '',
        sdi_rejection_errors: [],
        // receiver
        tipo_cliente: '',
        // business
        ragione_sociale: '',
        partita_iva: '',
        codice_destinatario: '',
        // privato
        nome: '',
        cognome: '',
        codice_fiscale: '',
        email: '',
        indirizzo: '',
        cap: '',
        citta: '',
        provincia: '',
    });

    const handleSelectChange = (selectedOption, name) => {
        const selectedValue = Array.isArray(selectedOption)
            ? selectedOption.map(option => option.value)
            : selectedOption?.value || '';

        setFormData(prev => ({
            ...prev,
            [name]: selectedValue,
        }));
    };

    const startEditReceiver = () => {
        setReceiverBackup(formData);
        setEditingReceiver(true);
    };

    const cancelEditReceiver = () => {
        if (receiverBackup) setFormData(receiverBackup);
        setEditingReceiver(false);
    };

    const downloadXml = () => {
        const blob = new Blob([formData.xml_document], { type: 'application/xml' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${(formData.invoice_number || 'fattura').replace(/[\\/:*?"<>|]/g, '-')}.xml`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
    };

    const saveReceiver = () => {
        // Dati mancanti o scritti male: si evidenzia il campo (regola «Dati mancanti»), niente bolla del browser
        const dati = formData;
        const vuoto = (v) => !String(v || '').trim();
        const lista = [];
        if (vuoto(dati.tipo_cliente)) lista.push({ campo: 'dest-tipo_cliente', messaggio: 'Manca il tipo di cliente' });
        if (dati.tipo_cliente === 'azienda') {
            if (vuoto(dati.ragione_sociale)) lista.push({ campo: 'dest-ragione_sociale', messaggio: 'Manca la ragione sociale' });
            if (vuoto(dati.partita_iva)) lista.push({ campo: 'dest-partita_iva', messaggio: 'Manca la partita IVA' });
            else if (String(dati.partita_iva).length !== 11 || isNaN(dati.partita_iva)) lista.push({ campo: 'dest-partita_iva', messaggio: `La partita IVA deve contenere esattamente 11 cifre numeriche: ne hai scritte ${String(dati.partita_iva).length}` });
            if (vuoto(dati.codice_destinatario)) lista.push({ campo: 'dest-codice_destinatario', messaggio: 'Manca il codice destinatario (SDI)' });
        }
        if (dati.tipo_cliente === 'privato') {
            if (vuoto(dati.nome)) lista.push({ campo: 'dest-nome', messaggio: 'Manca il nome' });
            if (vuoto(dati.cognome)) lista.push({ campo: 'dest-cognome', messaggio: 'Manca il cognome' });
        }
        if (vuoto(dati.codice_fiscale)) {
            lista.push({ campo: 'dest-codice_fiscale', messaggio: 'Manca il codice fiscale' });
        } else {
            const cf = String(dati.codice_fiscale);
            const cfRegex = /^[A-Z]{6}[0-9]{2}[A-Z][0-9]{2}[A-Z][0-9]{3}[A-Z]$/i;
            const valido = (cf.length === 16 && cfRegex.test(cf)) || (cf.length === 11 && !isNaN(cf));
            if (!valido) lista.push({ campo: 'dest-codice_fiscale', messaggio: 'Controlla il formato del codice fiscale (16 caratteri) o della partita IVA (11 cifre)' });
        }
        if (vuoto(dati.email)) lista.push({ campo: 'dest-email', messaggio: 'Manca la PEC o l\'email' });
        if (vuoto(dati.indirizzo)) lista.push({ campo: 'dest-indirizzo', messaggio: "Manca l'indirizzo" });
        if (vuoto(dati.cap)) lista.push({ campo: 'dest-cap', messaggio: 'Manca il CAP' });
        if (vuoto(dati.citta)) lista.push({ campo: 'dest-citta', messaggio: 'Manca la città' });
        if (vuoto(dati.provincia)) lista.push({ campo: 'dest-provincia', messaggio: 'Manca la provincia' });
        if (segnalaMancanti(lista) > 0) return;

        chiediConferma({ titolo: 'Aggiornare i dati del destinatario?', testo: 'I dati del destinatario saranno aggiornati e i documenti rigenerati.', conferma: 'Aggiorna', variante: 'primary' })
            .then((ok) => { if (ok) continueUpdate(); });
    };

    const continueUpdate = () => {


        const updateData = async () => {
            try {
                const dati_fatturazione = {
                    tax_code: formData.codice_fiscale,
                    pec: formData.email,
                    address: formData.indirizzo,
                    zip_code: formData.cap,
                    city: formData.citta,
                    province: formData.provincia,
                    nation: 'IT',
                    country_code: 'IT',
                }
                if(formData.tipo_cliente === 'privato') {
                    dati_fatturazione.type = 'consumer';
                    dati_fatturazione.first_name = formData.nome;
                    dati_fatturazione.last_name = formData.cognome;
                } else if(formData.tipo_cliente === 'azienda') {
                    dati_fatturazione.type = 'business';
                    dati_fatturazione.denomination = formData.ragione_sociale;
                    dati_fatturazione.vat_number = formData.partita_iva;
                    dati_fatturazione.sdi_code = formData.codice_destinatario;
                }

                const tokenDetailsString = sessionStorage.getItem('userDetails');
                if (tokenDetailsString) {
                    const tokenDetails = JSON.parse(tokenDetailsString);
                    const token = tokenDetails.idToken;
                    const apiClient = new MyApiClient();

                    setLoadingShowed(true);

                    if (formData.status_label === 'REJECTED') {
                        // Aggiorna dati di fatturazione del player
                        const updatePlayerResult = await apiClient.updateClubPlayerInvoiceDataReceiver(token, formData.playerId, dati_fatturazione);

                        if(updatePlayerResult && updatePlayerResult.error) {
                            setLoadingShowed(false);
                            setModalTitle("Errore durante l'operazione");
                            setModalText("Si è verificato un errore durante l'aggiornamento dei dati: " + updatePlayerResult.error);
                            setModalShowed(true);
                            return;
                        }

                        // Rigenera transazione
                        const regenerateResult = await apiClient.clubRegenerateTransactionInvoice(token, formData.id);
                        setLoadingShowed(false);

                        if(regenerateResult && regenerateResult.error) {
                            setModalTitle("Errore durante l'operazione");
                            setModalText("Si è verificato un errore durante la rigenerazione del documento: " + regenerateResult.error);
                            setModalShowed(true);
                        } else {
                            setEditingReceiver(false);
                            setModalTitle("Operazione completata");
                            setModalText("Il documento è stato rigenerato con successo. Ora puoi inviarlo nuovamente allo SDI.");
                            setModalShowed(true);
                            // Ricarica i dati per mostrare il nuovo stato NOT_SENT
                            setTimeout(() => {
                                setModalShowed(false);
                                setReloadKey((k) => k + 1);
                            }, 2000);
                        }
                    } else {
                        // Comportamento originale per NOT_SENT
                        const dataCreate = {
                            id: formData.id,
                            receiver: dati_fatturazione
                        }

                        const deleteResult = await apiClient.updateBookingTransaction(token, dataCreate);
                        setLoadingShowed(false);

                        if(!deleteResult || deleteResult.error) {
                            setModalTitle("Errore durante l'operazione");
                            setModalText(conMotivo("Si è verificato un errore durante l'aggiornamento"));
                            setModalShowed(true);
                        } else {
                            navigate('/struttura/booking/userTransactions');
                        }
                    }
                }
            } catch (error) {
                console.error("Error fetching data:", error);
            }
        };

        updateData();
    };

    // Invio allo SDI e rigenerazione dell'XML: prima stavano nel menu a fine riga della lista
    const sendToSdi = (row) => {
        if (!isFatturazioneConfirmed) {
            setModalTitle("Attenzione");
            setModalText("Non è possibile inviare la fattura allo SDI in quanto la richiesta di fatturazione elettronica per la tua struttura non è attiva o confermata.");
            setModalShowed(true);
            return;
        }
        chiediConferma({ titolo: 'Inviare la fattura allo SDI?', testo: 'La fattura verrà inviata allo SDI.', conferma: 'Invia', variante: 'primary' })
            .then((ok) => { if (ok) continueSendToSdi(row); });
    };

    const regenerateXml = (row) => {
        chiediConferma({ titolo: 'Rigenerare l\'XML della fattura?', testo: 'L\'XML della fattura verrà rigenerato.', conferma: 'Rigenera', variante: 'primary' })
            .then((ok) => { if (ok) continueRegenerateXml(row); });
    };

    const continueRegenerateXml = (row) => {


        const regenerateData = async () => {
            try {
                const tokenDetailsString = sessionStorage.getItem('userDetails');
                if (tokenDetailsString) {
                    const tokenDetails = JSON.parse(tokenDetailsString);
                    const token = tokenDetails.idToken;
                    const apiClient = new MyApiClient();

                    setLoadingShowed(true);
                    const regenerateResult = await apiClient.clubRegenerateTransactionInvoice(token, row.id);
                    setLoadingShowed(false);

                    if(regenerateResult && regenerateResult.error) {
                        setModalTitle("Errore durante l'operazione");
                        setModalText("Si è verificato un errore durante la rigenerazione dell'XML: " + regenerateResult.error);
                        setModalShowed(true);
                    } else {
                        setReloadKey((k) => k + 1);
                    }
                }
            } catch (error) {
                console.error("Error fetching data:", error);
            }
        };

        regenerateData();
    };

    const continueSendToSdi = (row) => {


        const sendData = async () => {
            try {
                const tokenDetailsString = sessionStorage.getItem('userDetails');
                if (tokenDetailsString) {
                    const tokenDetails = JSON.parse(tokenDetailsString);
                    const token = tokenDetails.idToken;
                    const apiClient = new MyApiClient();

                    setLoadingShowed(true);
                    const sendResult = await apiClient.sdiResendInvoice(token, row.id);
                    setLoadingShowed(false);

                    if(sendResult && sendResult.error) {
                        setModalTitle("Errore durante l'operazione");
                        setModalText("Si è verificato un errore durante l'invio allo SDI: " + sendResult.error);
                        setModalShowed(true);
                    } else {
                        setReloadKey((k) => k + 1);
                    }
                }
            } catch (error) {
                console.error("Error fetching data:", error);
            }
        };

        sendData();
    };

    // Il destinatario si modifica solo sulle fatture non ancora inviate o rifiutate dallo SDI
    const isDisabled = (formData.status_label !== 'NOT_SENT' && formData.status_label !== 'REJECTED') || formData.type_document === 'Ricevuta';


    // Documento così come arriva dall'API: intestazione, righe, cronologia
    const t = transaction || {};
    const isInvoice = t.type === 'INVOICE';
    const createdAt = toDate(t.createdAt);
    const invoiceDate = toDate(t.invoice_date);
    const documentTitle = [formData.type_document || 'Documento', formData.invoice_number ? `N. ${formData.invoice_number}` : ''].filter(Boolean).join(' ');
    // Titolo della pagina = il documento aperto; finché non è caricato resta quello generico
    usePageTitle(formData.id ? documentTitle : '');
    // Stato del documento in sola lettura subito dopo il titolo (regola SUI)
    useStatoInSolaLettura(formData.status?.label, formData.status_label === 'ACCEPTED' ? 'attivo' : ['REJECTED', 'NOT_SENT'].includes(formData.status_label) ? 'non-attivo' : 'bozza');
    const xmlData = useMemo(() => (isInvoice ? parseInvoiceXml(t.xml_document) : null), [isInvoice, t.xml_document]);
    const amount = toAmount(t.amount) ?? xmlData?.total ?? null;

    // Righe del documento: dall'XML se c'è, altrimenti una riga con l'importo
    const fallbackLabel = (t.user_subscription_id || t.subscription_id) ? 'Abbonamento' : 'Acquisto';
    // Il prodotto acquistato, dall'ordine del documento: nome, tipologia e numero d'ordine
    const TIPOLOGIA = { subscription: 'Abbonamento', course: 'Corso', lesson: 'Lezione', venue: 'Impianto' };
    const o = ordine?.dati;
    // Per chi è stato acquistato (22/10/2026): il junior del documento o dell'ordine, letto una volta per il box
    // «Dati allievo» e per la riga «per …» sotto l'articolo
    const juniorId = t.juniorId || o?.juniorId || '';
    const juniorName = o?.juniorName || '';
    const junior = useJunior(t.playerId || formData.playerId, juniorId, juniorName);
    const allievo = juniorId || juniorName ? nomeAllievo(junior, juniorName) : '';
    const nomeProdotto = o ? (o.subscription_name || o.course_name || o.lesson_name || o.venue_name || '') : '';
    const datiProdotto = ordine ? [TIPOLOGIA[ordine.tipo], o?.orderID ? `Ordine n. ${o.orderID}` : ''].filter(Boolean).join(' · ') : '';
    const lines = xmlData && xmlData.lines.length > 0
        ? xmlData.lines
        : [{ description: nomeProdotto || fallbackLabel, total: amount }];
    const unaRiga = lines.length === 1;
    const vatLabel = (line) => {
        if (line.vatRate === null || line.vatRate === undefined) return '';
        if (line.vatRate === 0) return ['IVA 0%', line.nature].filter(Boolean).join(' · ');
        return `IVA ${line.vatRate.toLocaleString('it-IT')}%`;
    };
    const lineMeta = (line) => [
        // Con una riga sola il prodotto e' quello dell'ordine
        unaRiga ? datiProdotto : '',
        line.quantity && line.quantity !== 1 ? `Qtà ${line.quantity.toLocaleString('it-IT')}` : '',
        vatLabel(line),
    ].filter(Boolean).join(' · ');
    const hasVatSummary = xmlData && xmlData.taxable !== null;

    // Cronologia: solo gli eventi di cui c'è il dato, dal più vecchio
    const status = formData.status_label;
    const rejectionErrors = Array.isArray(formData.sdi_rejection_errors) ? formData.sdi_rejection_errors : [];
    const sentToSdi = isInvoice && ['SENT', 'ACCEPTED', 'REJECTED'].includes(status);
    // Passi dell'invio allo SDI (solo fatture), dopo quelli di ordine, pagamento e documento
    const passiSdi = [
        sentToSdi && { key: 'sent', label: 'Inviato allo SDI' },
        isInvoice && status === 'ACCEPTED' && {
            key: 'accepted',
            label: 'Accettato dallo SDI',
            tone: 'is-success',
            detail: (formData.sdi_notification === 'MC' || formData.sdi_notification === 'AT')
                ? "Fattura messa a disposizione del destinatario sul cassetto fiscale dell'Agenzia delle Entrate."
                : '',
        },
        isInvoice && status === 'REJECTED' && { key: 'rejected', label: 'Rifiutato dallo SDI', tone: 'is-danger', errors: rejectionErrors },
    ].filter(Boolean);
    const documentoDellaCronologia = {
        transactionId: t.id,
        type: t.type,
        number: formData.invoice_number || t.invoice_number_str,
        date: t.invoice_date,
        createdAt: t.createdAt,
    };
    // Stessa cronologia della scheda dell'ordine (components/Vendite/CronologiaOrdine). Senza ordine collegato
    // (documenti vecchi) restano la registrazione e il documento.
    const timeline = o
        ? passiCronologia({ ordine: o, documento: documentoDellaCronologia, linkOrdine: `${SCHEDA_ORDINE[ordine.tipo]}${o.id}`, extra: passiSdi })
        : [
            createdAt && { key: 'created', label: 'Transazione registrata', date: createdAt, conOra: true },
            invoiceDate && { key: 'issued', label: 'Documento emesso', detail: formData.invoice_number ? `${formData.type_document || 'Documento'} n. ${formData.invoice_number}` : '', date: invoiceDate },
            ...passiSdi,
        ].filter(Boolean);

    const playerName = t.playerName || formData.playerName || '';

    // Dati del destinatario: solo le righe con un valore
    const isAzienda = formData.tipo_cliente === 'azienda';
    const recipientName = isAzienda
        ? formData.ragione_sociale
        : [formData.nome, formData.cognome].filter(Boolean).join(' ');
    const recipientRows = [
        ['Tipo', formData.tipo_cliente ? (isAzienda ? 'Azienda' : 'Privato') : ''],
        ['P. IVA', isAzienda ? formData.partita_iva : ''],
        ['Cod. fiscale', formData.codice_fiscale],
        ['SDI', isAzienda ? formData.codice_destinatario : ''],
        ['PEC / Email', formData.email],
        ['Indirizzo', formData.indirizzo],
        ['CAP', formData.cap],
        ['Città', formData.citta],
        ['Provincia', formData.provincia],
    ].filter(([, value]) => value);
    const hasRecipient = Boolean(recipientName) || recipientRows.length > 0;

    // Azioni del documento sulla riga del titolo, fuori dai box
    const slotAzioni = usePageHeaderActionsSlot(true);

    const textField = (name, label, type = 'text') => (
        <Form.Group className="mb-3">
            <Form.Label htmlFor={`dest-${name}`}>{label}</Form.Label>
            <Form.Control
                required
                id={`dest-${name}`}
                className="mt-2"
                type={type}
                name={name}
                value={formData[name] || ''}
                onChange={e => setFormData(prev => ({ ...prev, [name]: e.target.value }))}
            />
        </Form.Group>
    );

    return (
        <>
            <PageHeaderActions slot={slotAzioni}>
                {formData.pdf_document && <Button size="sm" variant="primary" className="sid-btn-compact" onClick={() => apriFile({ uri: formData.pdf_document, nome: `${(formData.invoice_number || 'documento').replace(/[\\/:*?"<>|]/g, '-')}.pdf`, tipo: 'application/pdf' })}>Apri PDF</Button>}
                {formData.type === 'INVOICE' && formData.xml_document && <Button size="sm" variant="primary" className="sid-btn-compact" onClick={() => downloadXml()}>Scarica XML</Button>}
            </PageHeaderActions>


            <Modal className="fade" show={modalShowed} centered>
                <Modal.Header>
                    <Modal.Title>{modalTitle}</Modal.Title>
                    <Button onClick={() => setModalShowed(false)} variant="" className="btn-close">
                    </Button>
                </Modal.Header>
                <Modal.Body>
                    <Row><Col className="col-xl-64">{modalText}</Col></Row>
                </Modal.Body>
                <Modal.Footer className="justify-content-between">
                    <Button className="btn btn-primary btn-add" onClick={() => setModalShowed(false)}>
                        Chiudi
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal className="fade" show={loadingShowed} centered>
                <Modal.Body className="text-center">
                <Spinner animation="border" role="status">
                    <span className="sr-only">Loading...</span>
                </Spinner>
                <div>Caricamento in corso...</div>
                </Modal.Body>
            </Modal>

            <Row className="g-4">
                <Col md={8} className="sid-order-col">
                    <Card className="sid-order-card">
                        <Card.Body>
                            <div className="sid-order-header">
                                <div className="sid-order-header__main">
                                    <div className="sid-order-header__title-row">
                                        <h2 className="sid-order-header__title">{documentTitle}</h2>
                                    </div>
                                    {invoiceDate && <div className="sid-order-header__meta">Emessa il {fmtDate(invoiceDate)}</div>}
                                </div>
                            </div>
                            <h5 className="sid-order-card__title">{lines.length > 1 ? 'Articoli' : 'Articolo'}</h5>
                            {lines.map((line, index) => (
                                <div key={index} className={`sid-line-item${index > 0 ? ' mt-3' : ''}`}>
                                    <div className="sid-line-item__thumb" aria-hidden="true">{(formData.type_document || 'D').charAt(0)}</div>
                                    <div className="sid-line-item__info">
                                        <div className="sid-line-item__name">{(unaRiga && nomeProdotto) || line.description || fallbackLabel}</div>
                                        {lineMeta(line) && <div className="sid-line-item__meta">{lineMeta(line)}</div>}
                                        {allievo && <div className="sid-line-item__meta">per {allievo}</div>}
                                    </div>
                                    <div className="sid-line-item__price">{fmtEuro(line.total)}</div>
                                </div>
                            ))}
                            <dl className="sid-order-totals">
                                {hasVatSummary && (
                                    <>
                                        <dt>Imponibile</dt>
                                        <dd>{fmtEuro(xmlData.taxable)}</dd>
                                        <dt>IVA</dt>
                                        <dd>{fmtEuro(xmlData.vat)}</dd>
                                    </>
                                )}
                                <dt className="is-total">Totale</dt>
                                <dd className="is-total">{fmtEuro(amount)}</dd>
                            </dl>
                        </Card.Body>
                    </Card>

                    <Card className="sid-order-card">
                        <Card.Body>
                            <h5 className="sid-order-card__title">Cronologia</h5>
                            <CronologiaOrdine passi={timeline} />
                        </Card.Body>
                    </Card>
                </Col>

                <Col md={4} className="sid-order-col">
                    <Card className="sid-order-card">
                        <Card.Body>
                            <DatiAllievo playerId={formData.playerId} playerName={playerName} juniorId={juniorId} juniorName={juniorName} junior={junior} />
                            <div className="sid-order-sep" aria-hidden="true" />
                            <h5 className="sid-order-card__title">Dati fatturazione</h5>
                            {editingReceiver && !isDisabled ? (
                                <Form ref={el => formRefs.current[0] = el} noValidate>
                                    <Form.Group className="mb-3">
                                        <Form.Label htmlFor="dest-tipo_cliente">Tipo cliente *</Form.Label>
                                        <Select
                                            inputId="dest-tipo_cliente"
                                            className="mt-2"
                                            name="tipo_cliente"
                                            styles={sidSelectStyles}
                                            value={optionsFatturazione.find(option => option.value === formData.tipo_cliente) || null}
                                            onChange={(selectedOption) => handleSelectChange(selectedOption, "tipo_cliente")}
                                            options={optionsFatturazione}
                                        />
                                    </Form.Group>
                                    {formData.tipo_cliente === 'azienda' && (
                                        <>
                                            {textField('ragione_sociale', 'Ragione sociale')}
                                            {textField('partita_iva', 'Partita IVA')}
                                            {textField('codice_destinatario', 'Codice destinatario (SDI)')}
                                        </>
                                    )}
                                    {formData.tipo_cliente === 'privato' && (
                                        <>
                                            {textField('nome', 'Nome')}
                                            {textField('cognome', 'Cognome')}
                                        </>
                                    )}
                                    {textField('codice_fiscale', 'Codice fiscale')}
                                    {textField('email', 'PEC / Email', 'email')}
                                    {textField('indirizzo', 'Indirizzo')}
                                    <Row className="g-3">
                                        <Col xs={5}>{textField('cap', 'CAP')}</Col>
                                        <Col xs={7}>{textField('citta', 'Città')}</Col>
                                    </Row>
                                    <Form.Group className="mb-3">
                                        <Form.Label htmlFor="dest-provincia">Provincia *</Form.Label>
                                        <Select
                                            inputId="dest-provincia"
                                            className="mt-2"
                                            name="provincia"
                                            styles={sidSelectStyles}
                                            value={italianProvinces.find(option => option.value === formData.provincia) || null}
                                            onChange={(selectedOption) => handleSelectChange(selectedOption, "provincia")}
                                            options={italianProvinces}
                                        />
                                    </Form.Group>
                                    <div className="sid-order-muted">Nazione: Italia</div>
                                    <div className="d-flex justify-content-end gap-2 mt-3">
                                        <Button size="sm" variant="dark" className="light sid-btn-compact" onClick={() => cancelEditReceiver()}>Annulla</Button>
                                        <Button size="sm" variant="primary" className="sid-btn-compact" onClick={() => saveReceiver()}>
                                            {formData.status_label === 'REJECTED' ? 'Salva e rigenera' : 'Salva'}
                                        </Button>
                                    </div>
                                </Form>
                            ) : (
                                <>
                                    {hasRecipient ? (
                                        <>
                                            <dl className="sid-recipient__list">
{recipientName && (<><dt>Nome</dt><dd>{recipientName}</dd></>)}
                                                {recipientRows.map(([label, value]) => (
                                                    <React.Fragment key={label}>
                                                        <dt>{label}</dt>
                                                        <dd>{value}</dd>
                                                    </React.Fragment>
                                                ))}
                                            </dl>
                                        </>
                                    ) : (
                                        <div className="sid-order-muted">Nessun dato di fatturazione</div>
                                    )}
                                    {!isDisabled && (
                                        <div className="d-flex justify-content-end mt-3">
                                            <Button size="sm" variant="outline-dark" className="sid-btn-compact" onClick={() => startEditReceiver()}>Modifica</Button>
                                        </div>
                                    )}
                                </>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {formData.status_label === 'NOT_SENT' && formData.type_document === 'Fattura' && (
                <div className="d-flex justify-content-end gap-4 mt-4">
                    <Button size="sm" variant="primary" className="light sid-btn-compact" onClick={() => regenerateXml(formData)}>Rigenera XML</Button>
                    <Button size="sm" variant="primary" className="sid-btn-compact" onClick={() => sendToSdi(formData)}>Invia allo SDI</Button>
                </div>
            )}
        </>
    )

}
export default UserTransactionsEdit;
